/**
 * Filtros del Plan: tres ejes que se cruzan (cuenta × dinero × gasto) y
 * filtros guardados con nombre. Los guardados van en la pref sincronizada
 * `asignar-filtros` como JSON `[{ id, nombre, cuenta, estado, gasto }]`, así
 * viajan entre dispositivos. Los filtros antiguos (listas de categorías) se
 * descartan al leer: el eje «Cuenta» los sustituye (Vadik, 22-oct-2026).
 */

/** Lista de categorías elegidas a mano (selector de pagos fijos, widgets). */
export type FiltroPropio = {
  id: string;
  nombre: string;
  categorias: string[];
};

/** Marca o desmarca una categoría en la selección del editor. */
export function alternarCategoria(
  seleccion: readonly string[],
  id: string,
): string[] {
  return seleccion.includes(id)
    ? seleccion.filter(c => c !== id)
    : [...seleccion, id];
}

/**
 * Aplica un predicado a los grupos: deja solo las categorías que lo cumplen
 * y quita los grupos que se quedan vacíos.
 */
export function filtrarGrupos<
  C extends { id: string },
  G extends { categories: C[] },
>(grupos: readonly G[], cumple: (categoria: C) => boolean): G[] {
  return grupos
    .map(g => ({ ...g, categories: g.categories.filter(cumple) }))
    .filter(g => g.categories.length > 0);
}

// ---------------------------------------------------------------------------
// Tres ejes: cuenta × dinero × gasto (se cruzan)
// ---------------------------------------------------------------------------

/** Eje «Dinero» (el gatillo de lado). */
export type EstadoFiltro =
  | 'ninguno'
  | 'infrafinanciadas'
  | 'gastado-de-mas'
  | 'sobrefinanciadas'
  | 'ignoradas'
  | 'sin-objetivo'
  | 'pagadas';

/** En el orden del gatillo, de arriba abajo. */
export const ESTADOS_FILTRO: readonly EstadoFiltro[] = [
  'ninguno',
  'infrafinanciadas',
  'gastado-de-mas',
  'sobrefinanciadas',
  'ignoradas',
  'sin-objetivo',
  'pagadas',
];

/** Eje «Gasto» (banderín): fijo = marcada como pago fijo. */
export type GastoFiltro = 'todo' | 'fijo' | 'variable';

export const GASTOS_FILTRO: readonly GastoFiltro[] = [
  'todo',
  'fijo',
  'variable',
];

/** Los tres ejes. `cuenta` = id de la cuenta o `null` (todas). */
export type EjesFiltro = {
  cuenta: string | null;
  estado: EstadoFiltro;
  gasto: GastoFiltro;
};

export const SIN_FILTRO: EjesFiltro = {
  cuenta: null,
  estado: 'ninguno',
  gasto: 'todo',
};

export type FiltroGuardado = EjesFiltro & { id: string; nombre: string };

/** ¿Hay algún eje puesto? */
export function hayFiltro(ejes: EjesFiltro): boolean {
  return (
    ejes.cuenta != null || ejes.estado !== 'ninguno' || ejes.gasto !== 'todo'
  );
}

export function mismosEjes(a: EjesFiltro, b: EjesFiltro): boolean {
  return a.cuenta === b.cuenta && a.estado === b.estado && a.gasto === b.gasto;
}

/** La opción que sigue a `actual` (y tras la última, la primera). */
export function siguiente<T>(lista: readonly T[], actual: T): T {
  const i = lista.indexOf(actual);
  return lista[(i + 1) % lista.length];
}

/** Lee la pref; ignora lo que no tenga forma de filtro guardado. */
export function leerGuardados(
  raw: string | null | undefined,
): FiltroGuardado[] {
  if (!raw) {
    return [];
  }
  let datos: unknown;
  try {
    datos = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(datos)) {
    return [];
  }
  const guardados: FiltroGuardado[] = [];
  for (const f of datos) {
    if (
      f &&
      typeof f === 'object' &&
      typeof f.id === 'string' &&
      typeof f.nombre === 'string' &&
      (f.cuenta === null || typeof f.cuenta === 'string') &&
      ESTADOS_FILTRO.includes(f.estado) &&
      GASTOS_FILTRO.includes(f.gasto)
    ) {
      guardados.push({
        id: f.id,
        nombre: f.nombre,
        cuenta: f.cuenta,
        estado: f.estado,
        gasto: f.gasto,
      });
    }
  }
  return guardados;
}

export function escribirGuardados(guardados: readonly FiltroGuardado[]) {
  return JSON.stringify(guardados);
}

/** Crea o sustituye (por id) un filtro guardado; nombre recortado. */
export function guardarFiltro(
  guardados: readonly FiltroGuardado[],
  filtro: FiltroGuardado,
): FiltroGuardado[] {
  const limpio = { ...filtro, nombre: filtro.nombre.trim() };
  const i = guardados.findIndex(f => f.id === filtro.id);
  if (i < 0) {
    return [...guardados, limpio];
  }
  return guardados.map(f => (f.id === filtro.id ? limpio : f));
}

export function borrarFiltro(
  guardados: readonly FiltroGuardado[],
  id: string,
): FiltroGuardado[] {
  return guardados.filter(f => f.id !== id);
}

/** Lo que hace falta saber de una categoría para filtrarla por dinero. */
export type DatosFiltro = {
  goal: number | null;
  longGoal: boolean;
  budgeted: number;
  balance: number;
  spent: number;
  ignorada?: boolean;
};

/**
 * ¿Cumple el estado? Sin datos (p. ej. ingresos) solo cumple «Sin filtro» o
 * «Pagadas» si lo está.
 */
export function cumpleEstado(
  estado: EstadoFiltro,
  datos: DatosFiltro | undefined,
  pagada: boolean,
  cumpleDinero: (
    filtro: 'infrafinanciadas' | 'sobrefinanciadas' | 'gastado-de-mas',
    datos: DatosFiltro,
  ) => boolean,
): boolean {
  switch (estado) {
    case 'ninguno':
      return true;
    case 'pagadas':
      return pagada;
    case 'ignoradas':
      return !!datos?.ignorada;
    case 'sin-objetivo':
      return datos != null && datos.goal == null;
    default:
      return datos != null && cumpleDinero(estado, datos);
  }
}

export type ContextoFiltro = {
  datos: ReadonlyMap<string, DatosFiltro>;
  pagadas: ReadonlySet<string>;
  /** Categorías marcadas como pago fijo. */
  fijos: ReadonlySet<string>;
  /** Cuenta de cada categoría (`null` = sin cuenta clara). */
  cuentaDe: (categoria: string) => string | null;
  cumpleDinero: Parameters<typeof cumpleEstado>[3];
};

/** ¿Se ve la categoría con estos ejes? (cuenta ∩ dinero ∩ gasto). */
export function seVe(
  id: string,
  ejes: EjesFiltro,
  ctx: ContextoFiltro,
): boolean {
  if (ejes.cuenta != null && ctx.cuentaDe(id) !== ejes.cuenta) {
    return false;
  }
  if (ejes.gasto !== 'todo' && ctx.fijos.has(id) !== (ejes.gasto === 'fijo')) {
    return false;
  }
  return cumpleEstado(
    ejes.estado,
    ctx.datos.get(id),
    ctx.pagadas.has(id),
    ctx.cumpleDinero,
  );
}

/** Cuántas se verían con cada estado de dinero, con la cuenta y el gasto puestos. */
export function contarPorEstado(
  ids: Iterable<string>,
  ejes: EjesFiltro,
  ctx: ContextoFiltro,
): Record<EstadoFiltro, number> {
  const r = Object.fromEntries(ESTADOS_FILTRO.map(e => [e, 0])) as Record<
    EstadoFiltro,
    number
  >;
  for (const id of ids) {
    for (const e of ESTADOS_FILTRO) {
      if (seVe(id, { ...ejes, estado: e }, ctx)) {
        r[e] += 1;
      }
    }
  }
  return r;
}

/**
 * Gatillo de lado: qué opción queda bajo el dedo. `y` es la altura del dedo
 * y el carril va de `arriba` a `abajo` (mismas unidades); fuera del carril,
 * la primera o la última.
 */
export function indiceEnCarril(
  y: number,
  arriba: number,
  abajo: number,
  n: number,
): number {
  if (n <= 1 || abajo <= arriba) {
    return 0;
  }
  const f = (y - arriba) / (abajo - arriba);
  return Math.max(0, Math.min(n - 1, Math.round(f * (n - 1))));
}
